export type Direction = 0 | 1 | 2 | 3;
export type PacketChannel = "cyan" | "violet" | "amber";
export type TileKind =
  | "source"
  | "server"
  | "straight"
  | "corner"
  | "router"
  | "oneway"
  | "cross"
  | "hub"
  | "turnstile"
  | "encryptor"
  | "firewall"
  | "filter";

export interface TileState {
  col: number;
  row: number;
  kind: TileKind;
  rotation: number;
  initialRotation: number;
  solutionRotation?: number;
  fixed?: boolean;
  channel?: PacketChannel;
}

export interface LevelDefinition {
  id: number | "custom";
  title: string;
  tip: string;
  columns: number;
  rows: number;
  tiles: TileState[];
}

export interface LogicalRoutePoint {
  col: number;
  row: number;
  encrypted: boolean;
  direction?: Direction;
  rotationAfter?: number;
}

export interface PacketRoute {
  channel: PacketChannel;
  points: LogicalRoutePoint[];
  success: boolean;
  reason: string;
  serverKey?: string;
}

export interface NetworkResult {
  routes: PacketRoute[];
  success: boolean;
  reason: string;
}

export interface SavedLevel {
  title: string;
  columns: number;
  rows: number;
  tiles: TileState[];
}

type Coord = [number, number];

interface SpecialTile {
  kind: TileKind;
  channel?: PacketChannel;
  fixed?: boolean;
}

interface RouteSpec {
  channel: PacketChannel;
  points: Coord[];
  specials?: Record<string, SpecialTile>;
}

interface ExtraTile {
  col: number;
  row: number;
  kind: TileKind;
  rotation?: number;
  fixed?: boolean;
  channel?: PacketChannel;
}

interface LevelSpec {
  title: string;
  tip: string;
  columns: number;
  rows: number;
  routes: RouteSpec[];
  decoys?: Coord[][];
  extras?: ExtraTile[];
  rotatable?: string[];
  initials?: Record<string, number>;
}

export const CHANNELS: PacketChannel[] = ["cyan", "violet", "amber"];
export const CHANNEL_COLORS: Record<PacketChannel, number> = {
  cyan: 0x62d7e8,
  violet: 0xb79aff,
  amber: 0xffc866
};
export const CHANNEL_CSS: Record<PacketChannel, string> = {
  cyan: "#62d7e8",
  violet: "#b79aff",
  amber: "#ffc866"
};
export const TILE_KINDS: TileKind[] = [
  "source",
  "server",
  "straight",
  "corner",
  "router",
  "oneway",
  "cross",
  "hub",
  "turnstile",
  "encryptor",
  "firewall",
  "filter"
];

export const DIRECTION_VECTORS: Record<Direction, { col: number; row: number }> = {
  0: { col: 0, row: -1 },
  1: { col: 1, row: 0 },
  2: { col: 0, row: 1 },
  3: { col: -1, row: 0 }
};

const key = (col: number, row: number): string => `${col}:${row}`;
const coordKey = ([col, row]: Coord): string => key(col, row);
const opposite = (direction: Direction): Direction =>
  ((direction + 2) % 4) as Direction;

const directionBetween = (from: Coord, to: Coord): Direction => {
  const col = to[0] - from[0];
  const row = to[1] - from[1];
  if (col === 1 && row === 0) return 1;
  if (col === -1 && row === 0) return 3;
  if (col === 0 && row === 1) return 2;
  if (col === 0 && row === -1) return 0;
  throw new Error(`Points ${from.join(",")} and ${to.join(",")} are not adjacent.`);
};

const rotationForConnections = (incoming: Direction, outgoing: Direction): number => {
  const wanted = new Set([incoming, outgoing]);
  for (let rotation = 0; rotation < 4; rotation += 1) {
    const candidate = new Set<Direction>([
      rotation as Direction,
      ((rotation + 1) % 4) as Direction
    ]);
    if ([...wanted].every((direction) => candidate.has(direction))) {
      return rotation;
    }
  }
  throw new Error("A corner cannot connect those directions.");
};

const solutionFor = (
  kind: TileKind,
  previous: Coord | undefined,
  current: Coord,
  next: Coord | undefined
): number => {
  if (kind === "source" && next) {
    return (directionBetween(current, next) + 3) % 4;
  }
  if (kind === "server" && previous) {
    const incoming = directionBetween(current, previous);
    return (incoming + 1) % 4;
  }
  if (!previous || !next) {
    return 0;
  }

  const incoming = directionBetween(current, previous);
  const outgoing = directionBetween(current, next);
  if (kind === "router") {
    return outgoing;
  }
  if (kind === "turnstile") {
    return outgoing;
  }
  if (kind === "oneway") {
    return (outgoing + 3) % 4;
  }
  if (kind === "cross" || kind === "hub") {
    return 0;
  }
  if (incoming === opposite(outgoing)) {
    return incoming % 2;
  }
  return rotationForConnections(incoming, outgoing);
};

const inferredKind = (
  previous: Coord | undefined,
  current: Coord,
  next: Coord | undefined
): TileKind => {
  if (!previous) return "source";
  if (!next) return "server";
  const incoming = directionBetween(current, previous);
  const outgoing = directionBetween(current, next);
  return incoming === opposite(outgoing) ? "straight" : "corner";
};

const makeLevel = (id: number, spec: LevelSpec): LevelDefinition => {
  const tiles = new Map<string, TileState>();

  for (const route of spec.routes) {
    route.points.forEach((current, index) => {
      const positionKey = coordKey(current);
      const previous = route.points[index - 1];
      const next = route.points[index + 1];
      const special = route.specials?.[positionKey];
      const kind = special?.kind ?? inferredKind(previous, current, next);
      const solution = solutionFor(kind, previous, current, next);
      const existing = tiles.get(positionKey);
      const candidateChannel =
        kind === "source" || kind === "server" || kind === "filter"
          ? special?.channel ?? route.channel
          : undefined;

      if (existing) {
        const sameSharedTile =
          existing.kind === kind &&
          (existing.solutionRotation === solution ||
            kind === "encryptor" ||
            kind === "cross" ||
            kind === "hub" ||
            kind === "turnstile") &&
          (existing.channel === undefined ||
            candidateChannel === undefined ||
            existing.channel === candidateChannel);
        if (sameSharedTile) {
          if (special?.fixed === false) existing.fixed = false;
          return;
        }
        if (
          (existing.kind === "cross" || existing.kind === "hub") &&
          (kind === "cross" || kind === "hub")
        ) {
          return;
        }
        throw new Error(`Level ${id} has conflicting routes at ${positionKey}.`);
      }

      const endpoint = kind === "source" || kind === "server";
      const fixed =
        special?.fixed ?? (kind === "straight" || kind === "corner");
      tiles.set(positionKey, {
        col: current[0],
        row: current[1],
        kind,
        rotation: solution,
        initialRotation: solution,
        solutionRotation: solution,
        fixed: endpoint ? true : fixed,
        channel:
          kind === "source" || kind === "server" || kind === "filter"
            ? special?.channel ?? route.channel
            : undefined
      });
    });
  }

  for (const extra of spec.extras ?? []) {
    const positionKey = key(extra.col, extra.row);
    if (!tiles.has(positionKey)) {
      tiles.set(positionKey, {
        col: extra.col,
        row: extra.row,
        kind: extra.kind,
        rotation: extra.rotation ?? 0,
        initialRotation: extra.rotation ?? 0,
        solutionRotation: extra.rotation ?? 0,
        fixed: extra.fixed ?? true,
        channel: extra.channel
      });
    }
  }

  for (const decoy of spec.decoys ?? []) {
    decoy.forEach((current, index) => {
      const positionKey = coordKey(current);
      if (tiles.has(positionKey) || index === 0 || index === decoy.length - 1) {
        return;
      }
      const previous = decoy[index - 1];
      const next = decoy[index + 1];
      const kind = inferredKind(previous, current, next);
      const rotation = solutionFor(kind, previous, current, next);
      tiles.set(positionKey, {
        col: current[0],
        row: current[1],
        kind,
        rotation,
        initialRotation: rotation,
        solutionRotation: rotation,
        fixed: true
      });
    });
  }

  for (const positionKey of spec.rotatable ?? []) {
    const item = tiles.get(positionKey);
    if (!item || item.kind === "source" || item.kind === "server") continue;
    item.fixed = false;
    const scramble = ((item.col * 3 + item.row * 5 + id) % 3) + 1;
    item.rotation = (item.solutionRotation! + scramble) % 4;
    item.initialRotation = item.rotation;
  }

  for (const [positionKey, rotation] of Object.entries(spec.initials ?? {})) {
    const item = tiles.get(positionKey);
    if (!item) {
      throw new Error(`Level ${id} initial rotation references ${positionKey}.`);
    }
    item.fixed = false;
    item.rotation = ((rotation % 4) + 4) % 4;
    item.initialRotation = item.rotation;
  }

  return {
    id,
    title: spec.title,
    tip: spec.tip,
    columns: spec.columns,
    rows: spec.rows,
    tiles: [...tiles.values()]
  };
};

export const getConnections = (item: TileState): Direction[] => {
  const rotation = ((item.rotation % 4) + 4) % 4;
  let base: Direction[];
  switch (item.kind) {
    case "source":
      base = [1];
      break;
    case "server":
      base = [3];
      break;
    case "corner":
      base = [0, 1];
      break;
    case "turnstile":
      base = [3, 0, 1];
      break;
    case "router":
    case "cross":
    case "hub":
    case "encryptor":
      base = [0, 1, 2, 3];
      break;
    case "oneway":
      base = [3, 1];
      break;
    default:
      base = [0, 2];
  }
  return base.map((direction) => ((direction + rotation) % 4) as Direction);
};

const channelName = (channel: PacketChannel): string =>
  channel.charAt(0).toUpperCase() + channel.slice(1);

const tracePacket = (
  source: TileState,
  tiles: TileState[],
  columns: number,
  rows: number,
  turnstileRotations: Map<string, number>
): PacketRoute => {
  const channel = source.channel ?? "cyan";
  const points: LogicalRoutePoint[] = [
    { col: source.col, row: source.row, encrypted: false }
  ];
  const sourceConnections = getConnections(source);
  let current = source;
  let direction = sourceConnections[0];
  let encrypted = false;
  const visited = new Set<string>([key(source.col, source.row)]);

  for (let step = 0; step < columns * rows * 2; step += 1) {
    const vector = DIRECTION_VECTORS[direction];
    const nextCol = current.col + vector.col;
    const nextRow = current.row + vector.row;
    const nextTile = tiles.find((item) => item.col === nextCol && item.row === nextRow);
    points.push({ col: nextCol, row: nextRow, encrypted, direction });

    if (
      nextCol < 0 ||
      nextCol >= columns ||
      nextRow < 0 ||
      nextRow >= rows ||
      !nextTile
    ) {
      return { channel, points, success: false, reason: `${channelName(channel)} hit a dead end.` };
    }

    const incoming = opposite(direction);
    const activeTurnstileRotation =
      nextTile.kind === "turnstile"
        ? turnstileRotations.get(key(nextTile.col, nextTile.row)) ?? nextTile.rotation
        : nextTile.rotation;
    const connections = getConnections({
      ...nextTile,
      rotation: activeTurnstileRotation
    });
    if (!connections.includes(incoming)) {
      return {
        channel,
        points,
        success: false,
        reason: `${channelName(channel)} reached a disconnected cable.`
      };
    }

    if (nextTile.kind === "oneway") {
      const allowedIncoming = ((3 + nextTile.rotation) % 4) as Direction;
      if (incoming !== allowedIncoming) {
        return {
          channel,
          points,
          success: false,
          reason: `${channelName(channel)} entered a one-way link backward.`
        };
      }
    }

    if (nextTile.kind === "filter" && nextTile.channel !== channel) {
      return {
        channel,
        points,
        success: false,
        reason: `${channelName(channel)} was rejected by a ${nextTile.channel} filter.`
      };
    }

    if (nextTile.kind === "firewall" && !encrypted) {
      return {
        channel,
        points,
        success: false,
        reason: `${channelName(channel)} reached a firewall before encryption.`
      };
    }

    if (nextTile.kind === "encryptor") {
      encrypted = true;
      points[points.length - 1].encrypted = true;
    }

    if (nextTile.kind === "server") {
      if (nextTile.channel !== channel) {
        return {
          channel,
          points,
          success: false,
          reason: `${channelName(channel)} reached the ${nextTile.channel} server.`
        };
      }
      return {
        channel,
        points,
        success: true,
        reason: `${channelName(channel)} delivered.`,
        serverKey: key(nextTile.col, nextTile.row)
      };
    }

    const positionKey = key(nextTile.col, nextTile.row);
    if (visited.has(positionKey)) {
      return {
        channel,
        points,
        success: false,
        reason: `${channelName(channel)} entered a loop.`
      };
    }
    visited.add(positionKey);

    let outgoing: Direction | undefined;
    if (nextTile.kind === "router") {
      outgoing = nextTile.rotation as Direction;
      if (outgoing === incoming) {
        return {
          channel,
          points,
          success: false,
          reason: `${channelName(channel)} was routed back where it came from.`
        };
      }
    } else if (nextTile.kind === "turnstile") {
      outgoing = activeTurnstileRotation as Direction;
      if (outgoing === incoming) {
        return {
          channel,
          points,
          success: false,
          reason: `${channelName(channel)} entered a rotator through its output.`
        };
      }
      const rotationAfter = (activeTurnstileRotation + 1) % 4;
      turnstileRotations.set(positionKey, rotationAfter);
      points[points.length - 1].rotationAfter = rotationAfter;
    } else if (
      nextTile.kind === "cross" ||
      nextTile.kind === "hub" ||
      nextTile.kind === "encryptor"
    ) {
      outgoing = direction;
    } else if (nextTile.kind === "oneway") {
      outgoing = ((1 + nextTile.rotation) % 4) as Direction;
    } else {
      outgoing = connections.find((candidate) => candidate !== incoming);
    }

    if (outgoing === undefined) {
      return {
        channel,
        points,
        success: false,
        reason: `${channelName(channel)} has nowhere to go.`
      };
    }

    current = nextTile;
    direction = outgoing;
  }

  return { channel, points, success: false, reason: `${channelName(channel)} entered a loop.` };
};

export const traceNetwork = (
  tiles: TileState[],
  columns: number,
  rows: number
): NetworkResult => {
  const sources = tiles.filter((item) => item.kind === "source");
  const servers = tiles.filter((item) => item.kind === "server");
  if (sources.length === 0) {
    return { routes: [], success: false, reason: "This level has no clients." };
  }

  const turnstileRotations = new Map(
    tiles
      .filter((item) => item.kind === "turnstile")
      .map((item) => [key(item.col, item.row), item.rotation])
  );
  const routes = sources.map((source) =>
    tracePacket(source, tiles, columns, rows, turnstileRotations)
  );
  const occupancy = new Map<string, { route: PacketRoute; point: LogicalRoutePoint }[]>();
  for (const route of routes) {
    for (const point of route.points.slice(1, -1)) {
      const pointKey = key(point.col, point.row);
      const entries = occupancy.get(pointKey) ?? [];
      entries.push({ route, point });
      occupancy.set(pointKey, entries);
    }
  }

  for (const [pointKey, entries] of occupancy) {
    if (entries.length < 2) continue;
    const [col, row] = pointKey.split(":").map(Number);
    const item = tiles.find((tileItem) => tileItem.col === col && tileItem.row === row);
    if (item?.kind === "hub") {
      return {
        routes,
        success: false,
        reason: "The shared hub overloaded when packets arrived together."
      };
    }
  }

  for (const [pointKey, entries] of occupancy) {
    if (entries.length < 2) continue;
    const [col, row] = pointKey.split(":").map(Number);
    const item = tiles.find((tileItem) => tileItem.col === col && tileItem.row === row);
    const axes = new Set(entries.map(({ point }) => (point.direction ?? 0) % 2));
    const isolatedCrossing = item?.kind === "cross" && axes.size === entries.length;
    const sharedEncryptor = item?.kind === "encryptor";
    const sharedTurnstile = item?.kind === "turnstile";
    const firstRoute = entries[0].route;
    const sameDestinationMerge =
      item?.kind !== "hub" &&
      firstRoute.serverKey !== undefined &&
      entries.every(
        ({ route }) =>
          route.channel === firstRoute.channel &&
          route.serverKey === firstRoute.serverKey
      );
    if (
      !isolatedCrossing &&
      !sharedEncryptor &&
      !sharedTurnstile &&
      !sameDestinationMerge
    ) {
      return {
        routes,
        success: false,
        reason:
          item?.kind === "hub"
            ? "The shared hub overloaded when packets arrived together."
            : "Two packet routes are sharing the same relay."
      };
    }
  }

  const failed = routes.find((route) => !route.success);
  if (failed) {
    return { routes, success: false, reason: failed.reason };
  }

  const usedServers = routes.map((route) => route.serverKey);
  if (servers.some((server) => !usedServers.includes(key(server.col, server.row)))) {
    return {
      routes,
      success: false,
      reason: "At least one server received nothing."
    };
  }

  return { routes, success: true, reason: "All packets delivered." };
};

const S = (kind: TileKind, fixed = true, channel?: PacketChannel): SpecialTile => ({
  kind,
  fixed,
  channel
});

const LEVEL_SPECS: LevelSpec[] = [
  {
    title: "Air gap",
    tip: "Firewalls reject packets that have not been encrypted.",
    columns: 7,
    rows: 5,
    routes: [
      {
        channel: "cyan",
        points: [[0, 2], [1, 2], [1, 1], [2, 1], [3, 1], [4, 1], [4, 2], [5, 2], [6, 2]],
        specials: {
          "1:2": S("router", false),
          "2:1": S("encryptor"),
          "4:1": S("router", false),
          "4:2": S("router", false),
          "5:2": S("firewall")
        }
      }
    ],
    extras: [{ col: 5, row: 1, kind: "firewall", rotation: 1, fixed: true }],
    decoys: [
      [[1, 2], [2, 2], [3, 2], [4, 2]],
      [[4, 1], [5, 1]],
      [[4, 2], [4, 3], [5, 3]]
    ],
    initials: { "1:2": 1, "4:1": 1, "4:2": 2 }
  },
  {
    title: "Wrong address",
    tip: "A server only accepts packets with its own color.",
    columns: 6,
    rows: 5,
    routes: [
      {
        channel: "cyan",
        points: [[0, 1], [1, 1], [1, 2], [2, 2], [3, 2], [4, 2], [4, 3], [5, 3]],
        specials: {
          "1:1": S("router", false),
          "3:2": S("cross"),
          "4:3": S("router", false)
        }
      },
      {
        channel: "violet",
        points: [[0, 3], [1, 3], [2, 3], [3, 3], [3, 2], [3, 1], [4, 1], [5, 1]],
        specials: {
          "3:3": S("router", false),
          "3:2": S("cross"),
          "3:1": S("router", false)
        }
      }
    ],
    decoys: [
      [[1, 1], [2, 1], [3, 1], [4, 1], [5, 1]],
      [[3, 3], [4, 3], [5, 3]]
    ],
    initials: { "1:1": 1, "3:3": 3 }
  },
  {
    title: "Round robin",
    tip: "The T rotates after routing a packet. Its arrow marks the output.",
    columns: 7,
    rows: 5,
    routes: [
      {
        channel: "cyan",
        points: [[2, 0], [2, 1], [2, 2], [3, 2], [4, 2], [5, 2], [6, 2]],
        specials: {
          "2:1": S("encryptor"),
          "2:2": S("turnstile", false),
          "3:2": S("firewall"),
          "4:2": S("filter", true, "cyan")
        }
      },
      {
        channel: "violet",
        points: [[0, 2], [1, 2], [2, 2], [2, 3], [2, 4]],
        specials: {
          "1:2": S("encryptor"),
          "2:2": S("turnstile", false),
          "2:3": S("firewall")
        }
      }
    ],
    initials: { "2:2": 0 }
  },
  {
    title: "Convergence",
    tip: "Packets headed to the same server may share a relay.",
    columns: 7,
    rows: 6,
    routes: [
      {
        channel: "cyan",
        points: [[0, 1], [1, 1], [2, 1], [2, 2], [3, 2], [4, 2], [5, 2], [6, 2]],
        specials: {
          "2:1": S("router", false),
          "2:2": S("router", false),
          "3:2": S("encryptor"),
          "4:2": S("firewall")
        }
      },
      {
        channel: "cyan",
        points: [[0, 4], [1, 4], [2, 4], [2, 3], [2, 2], [3, 2], [4, 2], [5, 2], [6, 2]],
        specials: {
          "2:4": S("router", false),
          "2:2": S("router", false),
          "3:2": S("encryptor"),
          "4:2": S("firewall")
        }
      }
    ],
    extras: [
      { col: 4, row: 1, kind: "firewall", rotation: 1, fixed: true },
      { col: 4, row: 4, kind: "firewall", rotation: 1, fixed: true }
    ],
    decoys: [
      [[2, 1], [3, 1], [4, 1], [5, 1], [5, 2]],
      [[2, 4], [3, 4], [4, 4], [5, 4], [5, 3], [5, 2]]
    ],
    initials: { "2:1": 1, "2:4": 3 }
  },
  {
    title: "Orthogonal",
    tip: "Encryptors preserve a packet's incoming direction.",
    columns: 7,
    rows: 7,
    routes: [
      {
        channel: "cyan",
        points: [[0, 2], [1, 2], [1, 3], [2, 3], [3, 3], [4, 3], [5, 3], [6, 3]],
        specials: {
          "1:2": S("router", false),
          "3:3": S("encryptor"),
          "4:3": S("firewall")
        }
      },
      {
        channel: "violet",
        points: [[4, 6], [4, 5], [3, 5], [3, 4], [3, 3], [3, 2], [3, 1], [3, 0]],
        specials: {
          "4:5": S("router", false),
          "3:3": S("encryptor"),
          "3:2": S("firewall")
        }
      }
    ],
    decoys: [
      [[1, 2], [2, 2]],
      [[4, 5], [4, 4]]
    ],
    extras: [
      { col: 2, row: 2, kind: "firewall", rotation: 1, fixed: true },
      { col: 4, row: 4, kind: "firewall", rotation: 0, fixed: true }
    ],
    initials: { "1:2": 1, "4:5": 2 }
  },
  {
    title: "Clock cycle",
    tip: "The second client reaches the T after the first has rotated it.",
    columns: 8,
    rows: 6,
    routes: [
      {
        channel: "cyan",
        points: [[3, 0], [3, 1], [3, 2], [4, 2], [5, 2], [6, 2], [7, 2]],
        specials: {
          "3:1": S("encryptor"),
          "3:2": S("turnstile", false),
          "5:2": S("firewall"),
          "6:2": S("filter", true, "cyan")
        }
      },
      {
        channel: "violet",
        points: [[0, 2], [1, 2], [2, 2], [3, 2], [3, 3], [3, 4], [4, 4], [5, 4], [6, 4], [7, 4]],
        specials: {
          "1:2": S("encryptor"),
          "3:2": S("turnstile", false),
          "3:3": S("router", false),
          "3:4": S("router", false),
          "5:4": S("firewall"),
          "6:4": S("filter", true, "violet")
        }
      }
    ],
    extras: [
      { col: 4, row: 3, kind: "firewall", rotation: 1, fixed: true },
      { col: 5, row: 5, kind: "firewall", rotation: 1, fixed: true }
    ],
    decoys: [
      [[3, 3], [4, 3]],
      [[3, 4], [3, 5], [4, 5], [5, 5]]
    ],
    initials: { "3:2": 3, "3:3": 1, "3:4": 2 }
  },
  {
    title: "Directed links",
    tip: "One-way links reject packets entering against the arrow.",
    columns: 8,
    rows: 5,
    routes: [
      {
        channel: "cyan",
        points: [[0, 1], [1, 1], [1, 2], [2, 2], [3, 2], [4, 2], [5, 2], [5, 3], [6, 3], [7, 3]],
        specials: {
          "1:1": S("router", false),
          "2:2": S("cross"),
          "3:2": S("oneway", false),
          "5:3": S("router", false)
        }
      },
      {
        channel: "violet",
        points: [[0, 3], [1, 3], [2, 3], [2, 2], [2, 1], [3, 1], [4, 1], [5, 1], [6, 1], [7, 1]],
        specials: {
          "2:3": S("router", false),
          "2:2": S("cross"),
          "3:1": S("oneway", false)
        }
      }
    ],
    decoys: [
      [[1, 1], [2, 1], [3, 1], [4, 1], [5, 1], [6, 1], [7, 1]],
      [[2, 3], [3, 3], [4, 3], [5, 3], [6, 3], [7, 3]]
    ],
    initials: { "1:1": 1, "2:3": 3, "3:2": 3, "3:1": 3 }
  },
  {
    title: "Cross traffic",
    tip: "Crosses keep horizontal and vertical traffic separate.",
    columns: 9,
    rows: 6,
    routes: [
      {
        channel: "cyan",
        points: [[0, 0], [1, 0], [1, 1], [1, 2], [1, 3], [1, 4], [2, 4], [3, 4], [4, 4], [5, 4], [6, 4], [7, 4], [7, 5], [8, 5]],
        specials: {
          "1:0": S("router", false),
          "1:2": S("cross"),
          "4:4": S("cross"),
          "3:4": S("filter", true, "cyan"),
          "7:5": S("router", false)
        }
      },
      {
        channel: "violet",
        points: [[0, 2], [1, 2], [2, 2], [3, 2], [4, 2], [5, 2], [6, 2], [6, 1], [6, 0], [7, 0], [8, 0]],
        specials: {
          "1:2": S("cross"),
          "6:2": S("router", false),
          "6:0": S("router", false),
          "3:2": S("filter", true, "violet")
        }
      },
      {
        channel: "amber",
        points: [[0, 5], [1, 5], [2, 5], [3, 5], [4, 5], [4, 4], [4, 3], [5, 3], [6, 3], [7, 3], [7, 2], [8, 2]],
        specials: {
          "4:5": S("router", false),
          "4:4": S("cross"),
          "7:2": S("router", false),
          "5:3": S("filter", true, "amber")
        }
      }
    ],
    decoys: [
      [[1, 0], [2, 0], [3, 0], [4, 0], [5, 0], [6, 0], [7, 0], [8, 0]],
      [[6, 2], [7, 2], [8, 2]],
      [[4, 5], [5, 5], [6, 5], [7, 5], [8, 5]]
    ],
    initials: { "1:0": 1, "6:2": 3, "4:5": 3 }
  },
  {
    title: "Fan-in",
    tip: "Every client must arrive; shared destinations allow shared relays.",
    columns: 9,
    rows: 7,
    routes: [
      {
        channel: "cyan",
        points: [[0, 1], [1, 1], [2, 1], [2, 2], [2, 3], [3, 3], [4, 3], [5, 3], [6, 3], [7, 3], [8, 3]],
        specials: {
          "2:1": S("router", false),
          "2:3": S("router", false),
          "4:3": S("encryptor"),
          "5:3": S("firewall")
        }
      },
      {
        channel: "cyan",
        points: [[0, 3], [1, 3], [2, 3], [3, 3], [4, 3], [5, 3], [6, 3], [7, 3], [8, 3]],
        specials: {
          "1:3": S("router", false),
          "2:3": S("router", false),
          "4:3": S("encryptor"),
          "5:3": S("firewall")
        }
      },
      {
        channel: "cyan",
        points: [[0, 5], [1, 5], [2, 5], [2, 4], [2, 3], [3, 3], [4, 3], [5, 3], [6, 3], [7, 3], [8, 3]],
        specials: {
          "2:5": S("router", false),
          "2:3": S("router", false),
          "4:3": S("encryptor"),
          "5:3": S("firewall")
        }
      }
    ],
    extras: [
      { col: 5, row: 1, kind: "firewall", rotation: 1, fixed: true },
      { col: 5, row: 5, kind: "firewall", rotation: 1, fixed: true }
    ],
    decoys: [
      [[2, 1], [3, 1], [4, 1], [5, 1], [6, 1]],
      [[2, 5], [3, 5], [4, 5], [5, 5], [6, 5]]
    ],
    initials: { "2:1": 1, "1:3": 0, "2:5": 3 }
  },
  {
    title: "Contention",
    tip: "A hub overloads when multiple packets use it during one send.",
    columns: 10,
    rows: 7,
    routes: [
      {
        channel: "cyan",
        points: [[0, 1], [1, 1], [1, 0], [2, 0], [3, 0], [4, 0], [5, 0], [6, 0], [7, 0], [8, 0], [9, 0], [9, 1]],
        specials: { "1:1": S("router", false), "5:0": S("filter", true, "cyan") }
      },
      {
        channel: "violet",
        points: [[0, 5], [1, 5], [1, 6], [2, 6], [3, 6], [4, 6], [5, 6], [6, 6], [7, 6], [8, 6], [9, 6], [9, 5]],
        specials: { "1:5": S("router", false), "5:6": S("filter", true, "violet") }
      },
      {
        channel: "amber",
        points: [[5, 5], [4, 5], [4, 4], [3, 4], [2, 4], [2, 3], [2, 2], [3, 2], [4, 2], [5, 2], [6, 2], [7, 2], [7, 3]],
        specials: {
          "4:5": S("router", false),
          "2:3": S("cross"),
          "5:2": S("filter", true, "amber")
        }
      }
    ],
    extras: [
      { col: 1, row: 3, kind: "router", rotation: 1, fixed: false },
      { col: 4, row: 3, kind: "hub", fixed: true }
    ],
    decoys: [
      [[1, 1], [1, 2], [1, 3], [2, 3], [3, 3], [4, 3], [5, 3], [6, 3], [7, 3]],
      [[1, 5], [1, 4], [1, 3], [2, 3], [3, 3], [4, 3]],
      [[4, 5], [4, 4], [4, 3]]
    ],
    initials: { "1:1": 2, "1:5": 0, "4:5": 0 }
  },
  {
    title: "Policy maze",
    tip: "Address, encryption, filter, and direction rules all apply.",
    columns: 9,
    rows: 7,
    routes: [
      {
        channel: "cyan",
        points: [[0, 1], [1, 1], [1, 2], [1, 3], [2, 3], [3, 3], [4, 3], [5, 3], [6, 3], [6, 4], [6, 5], [7, 5], [8, 5]],
        specials: {
          "1:1": S("router", false),
          "1:2": S("encryptor"),
          "2:3": S("cross"),
          "3:3": S("cross"),
          "4:3": S("firewall"),
          "5:3": S("oneway", false),
          "6:5": S("router", false)
        }
      },
      {
        channel: "violet",
        points: [[0, 5], [1, 5], [2, 5], [2, 4], [2, 3], [2, 2], [2, 1], [3, 1], [4, 1], [5, 1], [6, 1], [7, 1], [8, 1]],
        specials: {
          "2:5": S("router", false),
          "2:4": S("encryptor"),
          "2:3": S("cross"),
          "2:1": S("router", false),
          "3:1": S("firewall"),
          "5:1": S("filter", true, "violet")
        }
      },
      {
        channel: "amber",
        points: [[3, 6], [3, 5], [3, 4], [3, 3], [3, 2], [4, 2], [5, 2], [6, 2], [7, 2], [7, 3], [8, 3]],
        specials: {
          "3:5": S("router", false),
          "3:3": S("cross"),
          "5:2": S("filter", true, "amber")
        }
      }
    ],
    decoys: [
      [[1, 1], [2, 1], [3, 1], [4, 1], [5, 1], [6, 1], [7, 1], [8, 1]],
      [[2, 5], [3, 5], [4, 5], [5, 5], [6, 5], [7, 5], [8, 5]],
      [[3, 5], [4, 5], [5, 5], [6, 5]]
    ],
    initials: { "1:1": 1, "2:5": 3, "3:5": 3, "5:3": 3 }
  },
  {
    title: "Production network",
    tip: "Deliver all three packets without violating a network rule.",
    columns: 10,
    rows: 7,
    routes: [
      {
        channel: "cyan",
        points: [[0, 0], [1, 0], [1, 1], [1, 2], [1, 3], [1, 4], [1, 5], [1, 6], [2, 6], [3, 6], [4, 6], [5, 6], [6, 6], [7, 6], [8, 6], [9, 6]],
        specials: {
          "1:0": S("router", false),
          "1:2": S("encryptor"),
          "1:3": S("cross"),
          "1:5": S("cross"),
          "4:6": S("firewall"),
          "6:6": S("oneway", false)
        }
      },
      {
        channel: "violet",
        points: [[9, 0], [8, 0], [8, 1], [8, 2], [8, 3], [8, 4], [8, 5], [7, 5], [6, 5], [5, 5], [4, 5], [3, 5], [2, 5], [1, 5], [0, 5], [0, 6]],
        specials: {
          "8:0": S("router", false),
          "8:2": S("encryptor"),
          "8:3": S("cross"),
          "1:5": S("cross"),
          "5:5": S("firewall"),
          "3:5": S("oneway", false)
        }
      },
      {
        channel: "amber",
        points: [[0, 3], [1, 3], [2, 3], [3, 3], [4, 3], [5, 3], [6, 3], [7, 3], [8, 3], [9, 3]],
        specials: {
          "1:3": S("cross"),
          "3:3": S("encryptor"),
          "5:3": S("firewall"),
          "6:3": S("filter", true, "amber"),
          "8:3": S("cross")
        }
      }
    ],
    extras: [{ col: 4, row: 2, kind: "hub", fixed: true }],
    decoys: [
      [[1, 0], [2, 0], [3, 0], [4, 0], [5, 0], [6, 0], [7, 0], [8, 0]],
      [[1, 2], [2, 2], [3, 2], [4, 2], [5, 2], [6, 2], [7, 2], [8, 2]]
    ],
    initials: { "1:0": 1, "8:0": 1, "6:6": 3, "3:5": 1 }
  },
  {
    title: "Race condition",
    tip: "Each T keeps the state left by the previous numbered client.",
    columns: 10,
    rows: 7,
    routes: [
      {
        channel: "cyan",
        points: [[3, 0], [3, 1], [3, 2], [4, 2], [5, 2], [6, 2], [6, 3], [6, 4], [7, 4], [8, 4], [9, 4]],
        specials: {
          "3:1": S("encryptor"),
          "3:2": S("turnstile", false),
          "5:2": S("router", false),
          "6:4": S("turnstile", false),
          "7:4": S("filter", true, "cyan"),
          "8:4": S("firewall")
        }
      },
      {
        channel: "violet",
        points: [[0, 2], [1, 2], [2, 2], [3, 2], [3, 3], [3, 4], [2, 4], [1, 4], [0, 4]],
        specials: {
          "1:2": S("encryptor"),
          "3:2": S("turnstile", false),
          "3:4": S("router", false),
          "2:4": S("firewall"),
          "1:4": S("filter", true, "violet")
        }
      },
      {
        channel: "amber",
        points: [[4, 4], [5, 4], [6, 4], [6, 5], [7, 5], [8, 5], [9, 5]],
        specials: {
          "5:4": S("encryptor"),
          "6:4": S("turnstile", false),
          "7:5": S("firewall"),
          "8:5": S("filter", true, "amber")
        }
      }
    ],
    extras: [{ col: 5, row: 3, kind: "firewall", rotation: 0, fixed: true }],
    decoys: [
      [[5, 2], [5, 3]],
      [[3, 4], [4, 4]]
    ],
    initials: { "3:2": 0, "5:2": 2, "3:4": 1, "6:4": 0 }
  }
];

export const LEVELS: LevelDefinition[] = LEVEL_SPECS.map((spec, index) =>
  makeLevel(index + 1, spec)
);

export const cloneTiles = (tiles: TileState[]): TileState[] =>
  tiles.map((item) => ({
    ...item,
    rotation: item.initialRotation,
    initialRotation: item.initialRotation
  }));

for (const level of LEVELS) {
  const solved = level.tiles.map((item) => ({
    ...item,
    rotation: item.solutionRotation ?? item.rotation
  }));
  const result = traceNetwork(solved, level.columns, level.rows);
  if (!result.success) {
    throw new Error(`Level ${level.id} has an invalid solution: ${result.reason}`);
  }
}
